// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// QuickLog — Hands-Free typed free-text entry (T-203).
//
// A keyboard alternative to the voice Recorder: the user types a quick note
// ("flower 2 zone 1 runoff 250 ml pH 6.1"), picks a workflow (or leaves it on
// Auto-detect), and the same parse → resolve pipeline runs via hands-free-text.
// The resolved review item is handed to the parent exactly like the Recorder,
// so the ReviewCard handles the rest.

import { useCallback, useState } from "react";
import { AlertTriangle, Loader2, RotateCcw, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useFacility } from "@/contexts/FacilityContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { hfCard, hfCardHeader, hfCardTitle, hfWarnAlert, hfWarnBody, hfWarnTitle } from "./handsFreeUi";
import { cn } from "@/lib/utils";
import type { HandsFreeContext, PipelineResult, WorkflowType } from "./types";
import { WORKFLOW_LABELS, WORKFLOW_TYPES } from "./types";
import { callTextLog } from "./transcribe";

const MAX_LEN = 4000;

interface QuickLogProps {
  onPipelineResult: (result: PipelineResult) => void;
  onReset?: () => void;
  /** Pin the workflow and hide the selector (embedded mode). */
  fixedWorkflow?: WorkflowType;
  context?: HandsFreeContext;
}

export function QuickLog({ onPipelineResult, onReset, fixedWorkflow, context }: QuickLogProps) {
  const { activeFacility } = useFacility();

  const [text, setText] = useState("");
  const [workflow, setWorkflow] = useState<WorkflowType | "auto">(fixedWorkflow ?? "auto");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<PipelineResult | null>(null);

  const submit = useCallback(async () => {
    if (!activeFacility?.id || !text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    setPartial(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setError("Session expired. Please refresh the page and try again.");
        return;
      }
      const supabaseUrl =
        ((import.meta as Record<string, unknown>).env?.VITE_SUPABASE_URL as string | undefined) ||
        "https://bjvlynqjoetvrbbfvhod.supabase.co";

      const res = await callTextLog({
        text: text.trim(),
        facilityId: activeFacility.id,
        accessToken,
        supabaseUrl,
        workflowHint: workflow,
        context: context
          ? { ...context, ...(fixedWorkflow ? { workflow: fixedWorkflow } : {}) }
          : fixedWorkflow
            ? { workflow: fixedWorkflow }
            : undefined,
      });

      if (!res.ok) {
        setError(res.error);
        return;
      }

      const result: PipelineResult = {
        ...res.result,
        workflow_type: res.result.workflow_type ?? (workflow === "auto" ? undefined : workflow),
      };

      if (result.status === "parse_failed" || result.status === "resolve_failed") {
        setPartial(result);
        return;
      }
      onPipelineResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [activeFacility?.id, text, submitting, workflow, onPipelineResult]);

  const reset = useCallback(() => {
    setText("");
    setError(null);
    setPartial(null);
    onReset?.();
  }, [onReset]);

  if (!activeFacility) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No facility selected</AlertTitle>
            <AlertDescription>
              Select an active facility before logging. The entry is scoped to your
              current facility.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={hfCard}>
      <CardHeader className={hfCardHeader}>
        <CardTitle className={cn(hfCardTitle, "flex items-center gap-2")}>
          <Send className="h-4 w-4 text-primary" />
          Quick type
        </CardTitle>
        <CardDescription>
          Jot down a quick note — we'll turn it into a structured entry for you to review.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Workflow selector (hidden when pinned via fixedWorkflow) */}
        {!fixedWorkflow && (
          <div className="flex flex-wrap gap-1.5">
            {(["auto", ...WORKFLOW_TYPES] as const).map((w) => (
              <Button
                key={w}
                type="button"
                size="sm"
                variant={workflow === w ? "default" : "outline"}
                onClick={() => setWorkflow(w)}
                disabled={submitting}
                className="text-xs"
              >
                {w === "auto" ? "Auto-detect" : WORKFLOW_LABELS[w]}
              </Button>
            ))}
          </div>
        )}

        <div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            placeholder='e.g. "Flower 2 zone 1, 250 ml, pH 6.1, EC 2.4"'
            rows={4}
            className="resize-none text-sm"
            disabled={submitting}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
            }}
          />
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>⌘/Ctrl + Enter to submit</span>
            <span>{text.length}/{MAX_LEN}</span>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Couldn't process that</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {partial?.status === "parse_failed" && (
          <Alert className={hfWarnAlert}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className={hfWarnTitle}>Couldn't extract structured data</AlertTitle>
            <AlertDescription className={hfWarnBody}>
              Your note was saved but we couldn't pull structured fields from it.
              Try rephrasing with location and the key values.
            </AlertDescription>
          </Alert>
        )}

        {partial?.status === "resolve_failed" && (
          <Alert className={hfWarnAlert}>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className={hfWarnTitle}>Couldn't match entities</AlertTitle>
            <AlertDescription className={hfWarnBody}>
              The fields were extracted but the location couldn't be matched.
              Try including the exact names used in your facility.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          {(text || error || partial) && (
            <Button variant="outline" size="sm" onClick={reset} disabled={submitting}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
          <Button onClick={submit} disabled={submitting || !text.trim()} className="min-w-[120px]">
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Processing…
              </>
            ) : (
              <>
                <Send className="mr-1.5 h-4 w-4" />
                Extract &amp; review
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default QuickLog;
